import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Users,
  Settings,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  Eye,
  UserCheck,
  UserX,
  Key,
  Lock,
  Unlock,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  User,
  XCircle,
  Search
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  hierarchy_level: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

interface Permission {
  id: number;
  role_name: string;
  resource: string;
  action: string;
  conditions?: any;
}

interface UserRole {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login: string;
  projects_count?: number;
  created_at?: string;
  isLocked?: boolean;
  accountStatus?: string;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  lockedUsers: number;
}

interface ProjectPermission {
  id: number;
  project_id: number;
  user_id: number;
  permission_level: string;
  project_name: string;
  user_name: string;
  granted_by: number;
  granted_at: string;
}

const RoleManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'roles' | 'permissions' | 'users' | 'projects'>('users');
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [projectPermissions, setProjectPermissions] = useState<ProjectPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [editingUser, setEditingUser] = useState<UserRole | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // User management state
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const itemsPerPage = 10;

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Build user query params
      const userParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        include_role_stats: 'true',
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { isActive: statusFilter })
      });

      const [rolesRes, permissionsRes, usersRes, projectPermsRes, statsRes] = await Promise.all([
        api.get('/api/admin/roles'),
        api.get('/api/admin/permissions'),
        api.get(`/api/admin/users?${userParams}`),
        api.get('/api/admin/project-permissions'),
        api.get('/api/admin/statistics')
      ]);

      setRoles(rolesRes.data.roles || []);
      setPermissions(permissionsRes.data.permissions || []);
      setUserRoles(usersRes.data.users || []);
      setTotalPages(usersRes.data.pagination?.totalPages || 1);
      setTotalUsers(usersRes.data.pagination?.totalRecords || 0);
      setProjectPermissions(projectPermsRes.data.permissions || []);
      setUserStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching role data:', error);
      toast.error('Failed to load role management data');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Change user role
  const handleRoleChange = async (userId: number, newRole: string) => {
    if (!newRole) return;

    try {
      setActionLoading(userId);
      await api.put(`/api/admin/users/${userId}`, { role: newRole });
      toast.success('User role updated successfully');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update user role');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle user actions (toggle status, unlock, reset password, delete)
  const handleUserAction = async (userId: number, action: string) => {
    try {
      setActionLoading(userId);
      let response;

      switch (action) {
        case 'toggle-status':
          response = await api.post(`/api/admin/users/${userId}/toggle-status`);
          toast.success('User status updated successfully');
          break;

        case 'unlock':
          response = await api.post(`/api/admin/users/${userId}/unlock`);
          toast.success('User account unlocked successfully');
          break;

        case 'reset-password':
          response = await api.post(`/api/admin/users/${userId}/reset-password`, { generateRandom: true });
          const result = response.data;

          if (result.temporaryPassword) {
            const tempPasswordAlert = `Password reset successfully!\n\nTemporary password: ${result.temporaryPassword}\n\nPlease share this with the user securely and ask them to change it on first login.`;

            if (navigator.clipboard) {
              try {
                await navigator.clipboard.writeText(result.temporaryPassword);
                toast.success('Password reset! Temporary password copied to clipboard.');
              } catch {
                alert(tempPasswordAlert);
              }
            } else {
              alert(tempPasswordAlert);
            }
          }
          break;

        case 'delete':
          const user = userRoles.find(u => u.id === userId);
          const confirmMessage = `Are you sure you want to delete user "${user?.name || 'Unknown'}" (${user?.email || 'Unknown'})?\n\nThis action cannot be undone and will:\n- Permanently remove the user account\n- Remove access to all projects and documents\n- Clear all associated permissions\n\nType "DELETE" to confirm:`;

          const confirmation = prompt(confirmMessage);
          if (confirmation !== 'DELETE') {
            setActionLoading(null);
            return;
          }

          if (user?.role === 'SUPER_ADMIN') {
            toast.error('Cannot delete Super Admin accounts');
            setActionLoading(null);
            return;
          }

          response = await api.delete(`/api/admin/users/${userId}`);
          toast.success(`User "${response.data.deletedUser?.name || 'Unknown'}" has been deleted successfully`);
          break;

        default:
          setActionLoading(null);
          return;
      }

      fetchData();
    } catch (error: any) {
      console.error('Error performing action:', error);

      const errorData = error.response?.data;
      let errorMessage = 'An unexpected error occurred';

      if (errorData) {
        if (errorData.error && errorData.message) {
          errorMessage = `${errorData.error}: ${errorData.message}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  // Grant project access
  const handleGrantProjectAccess = async (projectId: number, userId: number, level: string) => {
    try {
      await api.post('/api/admin/project-permissions', {
        project_id: projectId,
        user_id: userId,
        permission_level: level
      });
      toast.success('Project access granted');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to grant project access');
    }
  };

  // Revoke project access
  const handleRevokeProjectAccess = async (permissionId: number) => {
    if (!window.confirm('Are you sure you want to revoke this project access?')) return;

    try {
      await api.delete(`/api/admin/project-permissions/${permissionId}`);
      toast.success('Project access revoked');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to revoke project access');
    }
  };

  const getRoleColor = (role: string) => {
    const colors = {
      'SUPER_ADMIN': 'bg-red-100 text-red-800',
      'ADMIN': 'bg-blue-100 text-blue-800',
      'CONSULTANT': 'bg-green-100 text-green-800',
      'READ_ONLY': 'bg-gray-100 text-gray-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getRoleDescription = (role: string) => {
    const descriptions = {
      'SUPER_ADMIN': 'Full system access with all permissions',
      'ADMIN': 'Administrative access with user and project management',
      'CONSULTANT': 'Standard user with project access and document management',
      'READ_ONLY': 'View-only access to assigned projects'
    };
    return descriptions[role as keyof typeof descriptions] || 'Unknown role';
  };

  const getPermissionLevelColor = (level: string) => {
    const colors = {
      'OWNER': 'bg-purple-100 text-purple-800',
      'ADMIN': 'bg-red-100 text-red-800',
      'EDIT': 'bg-blue-100 text-blue-800',
      'VIEW': 'bg-green-100 text-green-800'
    };
    return colors[level as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusIcon = (user: UserRole) => {
    if (user.isLocked) {
      return <Lock className="w-4 h-4 text-yellow-500" />;
    }
    if (user.is_active) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading role management...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-8 h-8 text-blue-600" />
          Users & Roles Management
        </h3>
        <p className="text-gray-600 mt-1">Manage users, roles, permissions, and project access</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'users', label: 'User Management', icon: Users },
            { id: 'roles', label: 'Role Overview', icon: Shield },
            { id: 'permissions', label: 'Permissions', icon: Key },
            { id: 'projects', label: 'Project Access', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Role Overview Tab */}
      {activeTab === 'roles' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {roles.map((role) => (
              <div key={role.id} className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center justify-between mb-3">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(role.name)}`}>
                    {role.display_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    Level {role.hierarchy_level}
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">{role.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {userRoles.filter(u => u.role === role.name).length} users
                  </span>
                  {role.is_system && (
                    <span className="text-blue-600 text-xs">System Role</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow border p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Role Hierarchy</h4>
            <div className="space-y-3">
              {roles
                .sort((a, b) => b.hierarchy_level - a.hierarchy_level)
                .map((role) => (
                  <div key={role.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-md">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Shield className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{role.display_name}</div>
                      <div className="text-sm text-gray-500">{role.description}</div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {userRoles.filter(u => u.role === role.name).length} users
                    </div>
                    <div className="text-sm text-blue-600 font-medium">
                      Level {role.hierarchy_level}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b border-gray-200">
              <h4 className="text-lg font-medium text-gray-900">Role Permissions</h4>
              <div className="mt-2">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a role to view permissions</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.display_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conditions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {permissions
                    .filter(p => !selectedRole || p.role_name === selectedRole)
                    .map((permission) => (
                      <tr key={permission.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {permission.resource}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {permission.action}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {permission.conditions ? (
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                              {JSON.stringify(permission.conditions)}
                            </code>
                          ) : (
                            <span className="text-gray-400">None</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* User Roles Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Statistics Cards */}
          {userStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Users</p>
                    <p className="text-2xl font-bold text-gray-900">{userStats.totalUsers}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Users</p>
                    <p className="text-2xl font-bold text-green-600">{userStats.activeUsers}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Inactive Users</p>
                    <p className="text-2xl font-bold text-gray-600">{userStats.inactiveUsers}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-gray-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Locked Users</p>
                    <p className="text-2xl font-bold text-yellow-600">{userStats.lockedUsers}</p>
                  </div>
                  <Lock className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <select
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Roles</option>
                {roles.map((role) => (
                  <option key={role.name} value={role.name}>
                    {role.display_name}
                  </option>
                ))}
              </select>
              <select
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setStatusFilter('');
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border rounded-md hover:bg-gray-50 flex items-center gap-2 justify-center"
              >
                <RefreshCw className="w-4 h-4" />
                Clear Filters
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4">
                        <div className="flex items-center justify-center h-32">
                          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                        </div>
                      </td>
                    </tr>
                  ) : userRoles.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    userRoles.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <User className="w-5 h-5 text-gray-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            disabled={actionLoading === user.id}
                            className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                          >
                            {roles.map((role) => (
                              <option key={role.name} value={role.name}>
                                {role.display_name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(user)}
                            <span className="text-sm text-gray-900">
                              {user.accountStatus || (user.isLocked ? 'Locked' : user.is_active ? 'Active' : 'Inactive')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(user.last_login)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            {user.isLocked && (
                              <button
                                onClick={() => handleUserAction(user.id, 'unlock')}
                                disabled={actionLoading === user.id}
                                className="text-yellow-600 hover:text-yellow-800 disabled:opacity-50"
                                title="Unlock Account"
                              >
                                <Unlock className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleUserAction(user.id, 'toggle-status')}
                              disabled={actionLoading === user.id}
                              className={`${user.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'} disabled:opacity-50`}
                              title={user.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleUserAction(user.id, 'reset-password')}
                              disabled={actionLoading === user.id}
                              className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              title="Reset Password"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            {user.role !== 'SUPER_ADMIN' && (
                              <button
                                onClick={() => handleUserAction(user.id, 'delete')}
                                disabled={actionLoading === user.id}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                title="Delete User"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page <span className="font-medium">{currentPage}</span> of{' '}
                        <span className="font-medium">{totalPages}</span> ({totalUsers} total users)
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === page
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Project Access Tab */}
      {activeTab === 'projects' && (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-900">Project Access Permissions</h4>
            <p className="text-sm text-gray-500 mt-1">
              Manage user access to specific projects
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permission Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Granted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projectPermissions.map((permission) => (
                  <tr key={permission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {permission.project_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {permission.user_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPermissionLevelColor(permission.permission_level)}`}>
                        {permission.permission_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(permission.granted_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleRevokeProjectAccess(permission.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Revoke Access"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;